/**
 * Robux Packs API (метод «Робукс паки», на бэке — logpass/Buffer).
 *
 * Заказы обрабатываются асинхронно: создали заказ → воркер отправил его в Buffer
 * → статус меняется по колбэку провайдера → фронт опрашивает
 * `get_logpass_history`. Креды аккаунта (пароль + резервные коды) собираются уже
 * ПОСЛЕ оплаты через `update_logpass_order`: заказ в статусе `paid` с
 * заполненными username + password + secret воркер подхватывает сам.
 *
 * Поставщик работает только в рублях — цена товара приходит одним числом в ₽,
 * поэтому паки везде считаются и показываются в ₽ независимо от валюты сайта.
 */

import http from './http'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://transfer.backend-pier.com'

export const PACK_CATEGORY = {
  ROBUX: 'robux',
  PREMIUM: 'premium'
}

export const PACK_STATUS = {
  NEW: 'new', // создан, ждёт оплаты через платёжку
  PAID: 'paid', // оплачен, в очереди на отправку в Buffer
  PROCESSING: 'processing', // отправлен в Buffer, идёт выполнение
  VERIFICATION: 'verification', // Buffer требует верификацию — есть verification_url
  SUCCESS: 'success', // выполнен, робуксы выданы
  CANCEL: 'cancel' // отменён/ошибка, деньги вернулись на баланс
}

/** Статусы, из которых заказ ещё может дойти до выдачи (есть смысл опрашивать). */
export const PACK_ACTIVE_STATUSES = [
  PACK_STATUS.NEW,
  PACK_STATUS.PAID,
  PACK_STATUS.PROCESSING,
  PACK_STATUS.VERIFICATION
]

/**
 * Номиналы robux, которые поддерживает Buffer. Остальные при оформлении вернут
 * ошибку [6], поэтому такие товары в выдачу не попадают.
 */
export const SUPPORTED_ROBUX_AMOUNTS = [
  80,
  160,
  240,
  320,
  500,
  740,
  820,
  1000,
  1240,
  1740,
  2240,
  3000,
  4000,
  5000,
  7000,
  8000,
  10000,
  14000,
  16000,
  20000,
  22500
]

// Коды ошибок make_logpass_order → ключи локализации.
const ORDER_ERROR_KEYS = {
  5: 'packs.errors.auth',
  1: 'packs.errors.product',
  6: 'packs.errors.amount',
  4: 'packs.errors.balance'
}

/** Ключ локализации для ответа `{ status: false, data: [<код>] }`. */
export function getPackOrderErrorKey(response) {
  const codes = Array.isArray(response?.data) ? response.data : []
  const code = codes.find(item => ORDER_ERROR_KEYS[item])

  return ORDER_ERROR_KEYS[code] || 'payment.errors.server'
}

/** Заказу нужны данные аккаунта: оплачен, но креды ещё не заполнены. */
export function isPackWaitingCredentials(order) {
  if (!order || order.status !== PACK_STATUS.PAID) return false

  return !(order.password && order.secret)
}

/**
 * Нормализация товара к виду, который понимают карточки выбора (GiftCard):
 * price — объект по валютам, quantity — количество робуксов.
 */
function normalizePack(product) {
  return {
    id: product.id,
    quantity: Number(product.details?.amount) || 0,
    name: product.name,
    name_en: product.name_en,
    category: product.category,
    platform: product.platform || 'roblox',
    image_url: product.image_url,
    stock: true,
    price: { rub: Number(product.price) || 0 }
  }
}

/** Товар можно заказать: включён и его номинал поддерживает Buffer. */
function isOrderable(product) {
  if (!product.enabled) return false
  if (product.category !== PACK_CATEGORY.ROBUX) return true

  return SUPPORTED_ROBUX_AMOUNTS.includes(Number(product.details?.amount))
}

function splitPacks(products) {
  const packs = products.filter(isOrderable).map(normalizePack)
  const byAmount = (a, b) => a.quantity - b.quantity

  return {
    robux: packs.filter(pack => pack.category === PACK_CATEGORY.ROBUX).sort(byAmount),
    premium: packs.filter(pack => pack.category === PACK_CATEGORY.PREMIUM).sort(byAmount)
  }
}

/** Загрузка паков, разбитых на robux / premium. */
export async function getPacks() {
  const { data } = await http.get(`${API_BASE_URL}/products?platform=roblox`)

  if (!data || !Array.isArray(data.products)) return { robux: [], premium: [] }

  return splitPacks(data.products)
}

/**
 * Создать заказ на пак.
 * flag=true — оплата платёжной системой (заказ создаётся в статусе `new`, в
 * `paid` его переведёт бэк после подтверждения оплаты). Без flag — списываем
 * с баланса, заказ сразу `paid`.
 *
 * Пароль и резервные коды на этом шаге ещё не собраны — их дозаполняет
 * `updatePackOrder` после оплаты, поэтому отправляем только то, что есть.
 */
export async function makePackOrder({ productId, username, password, secret, flag, token }) {
  const params = new URLSearchParams({ product_id: productId, username })

  if (password) params.set('password', password)
  if (secret) params.set('secret', secret)
  // Важно: само наличие параметра означает «не списывать с баланса».
  if (flag) params.set('flag', 'true')

  const { data } = await http.get(`${API_BASE_URL}/make_logpass_order?${params.toString()}`, token)

  return data || {}
}

/**
 * Заполнить/поменять креды заказа. Возвращает { status, forbidden }:
 * forbidden — заказ не найден, не принадлежит пользователю либо находится
 * в неизменяемом статусе (`success` / `cancel` / `new`).
 */
export async function updatePackOrder({ orderId, username, password, secret, token }) {
  const params = new URLSearchParams({ order_id: orderId })

  if (username) params.set('username', username)
  if (password) params.set('password', password)
  if (secret) params.set('secret', secret)

  const { statusCode, data } = await http.get(`${API_BASE_URL}/update_logpass_order?${params.toString()}`, token)

  if (statusCode === 403) return { status: false, forbidden: true }

  return data || { status: false }
}

/** История заказов паков. isActive — только активные заказы (для опроса статуса). */
export async function getPacksHistory({ page = 1, count = 7, isActive = false, platform = '', token } = {}) {
  const params = new URLSearchParams({ page, count })

  if (isActive) params.set('is_active', 'true')
  if (platform) params.set('platform', platform)

  const { data } = await http.get(`${API_BASE_URL}/get_logpass_history?${params.toString()}`, token)

  if (!data || !data.status) return { status: false, orders: [], total_orders: 0, total_pages: 1 }

  return {
    status: true,
    orders: data.orders || [],
    total_orders: data.total_orders || 0,
    total_pages: data.total_pages || 1
  }
}
